import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Truck, TruckStatus, TruckFuelType } from '../types/admin';

interface TrucksResponse {
  success: boolean;
  data: Truck[];
}

interface TruckResponse {
  success: boolean;
  data: Truck;
}

export interface TruckInput {
  name: string;
  type?: string;
  year?: number;
  make?: string;
  model?: string;
  licensePlate?: string;
  capacity?: number;
  weightLimit?: number;
  currentLocation?: string;
  status?: TruckStatus;
  fuelType?: TruckFuelType;
  notes?: string;
}

export function useOrgTrucks(orgId: string) {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrucks = useCallback(() => {
    if (!orgId) return Promise.resolve();
    setLoading(true);
    return api
      .get<TrucksResponse>(`/org-admin/${orgId}/trucks`)
      .then(res => {
        setTrucks(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load trucks');
        setLoading(false);
      });
  }, [orgId]);

  useEffect(() => {
    fetchTrucks();
  }, [fetchTrucks]);

  const createTruck = useCallback(
    async (input: TruckInput) => {
      const res = await api.post<TruckResponse>(`/org-admin/${orgId}/trucks`, input);
      setTrucks(prev => [...prev, res.data]);
      return res.data;
    },
    [orgId],
  );

  const updateTruck = useCallback(
    async (truckId: string, input: Partial<TruckInput>) => {
      const res = await api.patch<TruckResponse>(`/org-admin/${orgId}/trucks/${truckId}`, input);
      setTrucks(prev => prev.map(t => t.id === truckId ? res.data : t));
      return res.data;
    },
    [orgId],
  );

  const deleteTruck = useCallback(
    async (truckId: string) => {
      await api.del<{ success: boolean }>(`/org-admin/${orgId}/trucks/${truckId}`);
      setTrucks(prev => prev.filter(t => t.id !== truckId));
    },
    [orgId],
  );

  return { trucks, loading, error, createTruck, updateTruck, deleteTruck, refetch: fetchTrucks };
}
